import { ArrowDownDark, IcoUploadDocument } from '@icons';
import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import UploadButton from './UploadButton';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { projectMassiveUpload, projectUploadChecklist } from '@lib/api';
import { useParams } from 'react-router-dom';
import { RejectModal, SuccessModal } from '@components/atomic/organisms';
import { useMassiveDocumentsUpload } from './useMassiveDocumentsUpload';
import UploadDataButton, { UploadChoiceType } from './UploadDataButton';
import ImportJobToolModal from './ImportJobToolModal';
import UploadChoiceModal from './UploadChoiceModal';
import Banner from '@components/atomic/atoms/Banner/Banner';
import { useHasPermission } from '@lib/hooks/useHasPermission';
interface UploadEntitiesModalProps {
	entity: {
		translate: string;
		entityType: string;
	};
	hasEnabledAi: boolean;
}

const UploadEntitiesModal: React.FC<UploadEntitiesModalProps> = ({ entity, hasEnabledAi }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isJobToolModalOpen, setIsJobToolModalOpen] = useState(false);
	const [selectedFileForUploadChoice, setSelectedFileForUploadChoice] = useState<File>();
	const [isTooBig, setIsTooBig] = useState(false);
	const { projectId } = useParams<{ projectId: string }>();
	const canViewAIDisclaimer = useHasPermission(['view-ai-disclaimer'], 'all');

	const queryClient = useQueryClient();

	const uploadDataMutation = useMutation({
		mutationFn: async (params: { file: File; uploadType: UploadChoiceType; sal?: string }) => {
			const result = await projectMassiveUpload(entity.entityType, projectId, params.file, params.uploadType, params.sal);
			if (result.success) return result;

			if (result.message === 'Validation errors') {
				if (result.data.file) {
					throw new Error(result.data.file.join('\n'));
				}
				const errorMessages = Object.values(result.data)
					.flat()
					.map((item: any) =>
						item?.colonna && Array.isArray(item?.errore) ? `- Colonna ${item.colonna}: ${item.errore.join(', ')}` : item,
					)
					.join('\n');

				throw new Error(errorMessages || 'Upload failed');
			}

			throw new Error(result.message || 'Upload failed');
		},
		onError: (error) => {
			console.error('Upload failed:', error);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['batches', projectId] });
			queryClient.invalidateQueries({ queryKey: ['entities', entity.entityType, projectId] });
			queryClient.invalidateQueries({ queryKey: ['staging-entities', entity.entityType, projectId] });
			queryClient.invalidateQueries({ queryKey: ['staging-checklists', entity.entityType, projectId] });
			queryClient.invalidateQueries({ queryKey: ['staging-docs', entity.entityType, projectId] });
		},
	});

	const uploadChecklistMutation = useMutation({
		mutationFn: async (params: { file: File }) => {
			const cleanedEntityType = entity.entityType !== 'payments' ? entity.entityType : 'invoicepayments';
			const result = await projectUploadChecklist(cleanedEntityType, projectId, params.file);

			if (result.success) return result;

			if (result.message === 'Validation errors') {
				const errorMessages = Object.values(result.data)
					.flat()
					.map((item: any) =>
						item?.colonna && Array.isArray(item?.errore) ? `- Colonna ${item.colonna}: ${item.errore.join(', ')}` : item,
					)
					.join('\n');

				throw new Error(errorMessages || 'Upload failed');
			}

			throw new Error(result.message || 'Upload failed');
		},
		onError: (error) => {
			console.error('Upload failed:', error);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['batches', projectId] });
			queryClient.invalidateQueries({ queryKey: ['entities', entity.entityType, projectId] });
			queryClient.invalidateQueries({ queryKey: ['staging-entities', entity.entityType, projectId] });
			queryClient.invalidateQueries({ queryKey: ['staging-checklists', entity.entityType, projectId] });
			queryClient.invalidateQueries({ queryKey: ['staging-docs', entity.entityType, projectId] });
		},
	});

	const { mutation: uploadDocumentsMutation, progress: uploadDocumentsMutationProgress } = useMassiveDocumentsUpload();

	const getEntityDescriptionPartByEntityKey = (key: string) => {
		switch (key) {
			case 'contracts':
				return 'degli incarichi e attingimenti';
			case 'invoices':
				return 'delle fatture';
			case 'payments':
				return 'dei pagamenti';
			case 'timesheets':
				return 'del personale';
			default:
				return 'UNKNOWN KEY';
		}
	};

	const getChecklistEntityDescriptionPartByEntityKey = (key: string) => {
		switch (key) {
			case 'contracts':
				return 'ad incarichi e attingimenti';
			case 'invoices':
				return 'alle fatture';
			case 'payments':
				return 'ai pagamenti';
			case 'timesheets':
				return 'al personale';
			default:
				return 'UNKNOWN KEY';
		}
	};

	const getDocumentDescriptionPartByEntityKey = (key: string) => {
		switch (key) {
			case 'contracts':
				return 'contratti, determine o documentazione di supporto';
			case 'invoices':
				return 'le fatture e la documentazione di supporto';
			case 'payments':
				return 'le quietanze, mandati o giustificativi di pagamento';
			case 'timesheets':
				return 'i contratti, cedolini o documenti collegati alle anagrafiche del personale';
			default:
				return 'UNKNOWN KEY';
		}
	};

	const itemsToUpload = [
		{
			key: `${entity.entityType}-upload`,
			label: `Carica i dati`,
			description: `Importa il template compilato con i dati ${getEntityDescriptionPartByEntityKey(entity.entityType)} da caricare in piattaforma. La piattaforma popolerà automaticamente i dati e aggiornerà lo stato di avanzamento del progetto.`,
			mutation: uploadDataMutation,
			accepts: '.xls, .xlsx',
			successMessage: "Dati caricati con successo. Controlla la tab dei log per verificare l'esito dell'importazione.",
		},
		{
			key: `checklist-${entity.entityType}-upload`,
			label: `Carica la checklist`,
			description: `Importa il file excel per aggiornare massivamente lo stato delle attività di assurance relativo ${getChecklistEntityDescriptionPartByEntityKey(entity.entityType)}. Il file consente di riallineare o correggere le verifiche già svolte.`,
			mutation: uploadChecklistMutation,
			accepts: '.xls, .xlsx',
			successMessage: "Checklist caricata con successo. Controlla la tab dei log per verificare l'esito dell'importazione.",
		},
		{
			key: `documents-${entity.entityType}-upload`,
			label: `Carica i documenti allegati`,
			description: `Carica una cartella ZIP contenente ${getDocumentDescriptionPartByEntityKey(entity.entityType)}. La piattaforma  collegherà automaticamente la documentazione ai record già importati.`,
			mutation: uploadDocumentsMutation,
			progress: uploadDocumentsMutationProgress,
			accepts: '.zip',
			successMessage: "Documenti caricati con successo. Controlla la tab dei log per verificare l'esito dell'importazione.",
		},
	];

	const errors = itemsToUpload
		.map((item) => ({ key: item.key, show: item.mutation.isError, message: item.mutation.error?.message, onHide: item.mutation.reset }))
		.filter((error) => error.show);

	const successes = itemsToUpload
		.map((item) => ({
			key: item.key,
			show: item.mutation.isSuccess,
			onHide: () => {
				item.mutation.reset();
				queryClient.invalidateQueries({ queryKey: ['batches', projectId] });
				queryClient.invalidateQueries({ queryKey: ['entities', entity.entityType, projectId] });
				queryClient.invalidateQueries({ queryKey: ['staging-entities', entity.entityType, projectId] });
				queryClient.invalidateQueries({ queryKey: ['staging-checklists', entity.entityType, projectId] });
				queryClient.invalidateQueries({ queryKey: ['staging-docs', entity.entityType, projectId] });
			},
			successMessage: item.successMessage,
		}))
		.filter((success) => success.show);

	const mainModalShow =
		isModalOpen && errors.length === 0 && successes.length === 0 && !selectedFileForUploadChoice && !isTooBig && !isJobToolModalOpen;

	return (
		<>
			<Button
				onClick={() => {
					setIsModalOpen(true);
				}}
				className="btn custom-ghost-button"
				style={{
					fontWeight: 500,
					fontSize: '18px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '0.5rem',
				}}
			>
				<IcoUploadDocument
					style={{
						width: '24px',
						height: '24px',
					}}
				/>

				<span style={{ translate: '0 1px' }}>Importa</span>
			</Button>

			<Modal
				show={mainModalShow}
				onHide={() => {
					setIsModalOpen(false);
				}}
				centered
				size="xl"
				className="custom-modal-xl"
			>
				<Modal.Header
					style={{
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '1rem',
						padding: '1.5rem',
					}}
				>
					<IcoUploadDocument
						style={{
							fill: 'var(--np-primary-color)',
						}}
					/>
					<Modal.Title>
						<h5
							style={{
								fontWeight: 700,
								fontSize: '24px',
								textAlign: 'center',
								marginBottom: 0,
							}}
						>
							Importa {entity.translate}
						</h5>
					</Modal.Title>

					<div>
						<p
							style={{
								marginBottom: 0,
								textAlign: 'center',
							}}
						>
							Scegli un file da caricare.
						</p>
					</div>
				</Modal.Header>
				<Modal.Body
					style={{
						padding: hasEnabledAi && canViewAIDisclaimer ? '0 1.5rem' : '1.5rem',
					}}
				>
					<div
						style={{
							display: 'grid',
							gap: '1rem',
						}}
					>
						{hasEnabledAi && canViewAIDisclaimer ? (
							<Banner
								variant="warning"
								message={
									'Al caricamento delle informazioni, verrà avviato automaticamente il controllo automatizzato basato su Intelligenza Artificiale per la verifica dei dati e dei documenti caricati massivamente.'
								}
								className="f-600"
							/>
						) : null}

						{itemsToUpload.map((item) => (
							<div
								key={item.key}
								style={{
									padding: '1rem',
									border: '1px solid #2A3A64',
									borderRadius: '8px',
									display: 'grid',
									gridTemplateColumns: '1fr auto',
									gap: '3rem',
									alignItems: 'center',
								}}
							>
								<div>
									<span
										style={{
											fontWeight: 700,
											display: 'block',
										}}
									>
										{item.label}
									</span>
									<span
										style={{
											display: 'block',
										}}
									>
										{item.description}
									</span>
								</div>
								<div>
									{item.key === `${entity.entityType}-upload` && entity.entityType !== 'payments' ? (
										<UploadDataButton itemToUpload={item} setSelectedFile={setSelectedFileForUploadChoice} />
									) : (
										<UploadButton itemToUpload={item} setIsTooBig={setIsTooBig} />
									)}
									{/* <Button
										className="btn custom-primary-button"
										style={{
											height: '48px',
											fontWeight: 500,
											padding: '12px 24px',
											display: 'flex',
											alignItems: 'center',
											gap: '0.5rem',
										}}
									>
										<ArrowDownDark
											style={{
												rotate: '180deg',
											}}
										/>
										Carica file
									</Button> */}
								</div>
							</div>
						))}

						{entity.entityType === 'timesheets' ? (
							<div
								style={{
									padding: '1rem',
									border: '1px solid #2A3A64',
									borderRadius: '8px',
									display: 'grid',
									gridTemplateColumns: '1fr auto',
									gap: '3rem',
									alignItems: 'center',
								}}
							>
								<div>
									<span
										style={{
											fontWeight: 700,
											display: 'block',
										}}
									>
										Import dati personale
									</span>
									<span
										style={{
											display: 'block',
										}}
									>
										Importa i dati aggiornati relativi ai costi del personale per il periodo d'interesse.
									</span>
								</div>
								<div>
									<Button
										onClick={() => {
											setIsJobToolModalOpen(true);
										}}
										// className='mx-3' style={{ width: "auto", marginBottom: "8px" }}
										className="btn custom-primary-button"
										style={{
											height: '48px',
											fontWeight: 500,
											padding: '12px 24px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											gap: '0.5rem',
											minWidth: '185px',
										}}
									>
										<ArrowDownDark />
										Importa
									</Button>
								</div>
							</div>
						) : null}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button
						className="btn custom-outline-button"
						style={{
							height: '48px',
						}}
						onClick={() => {
							setIsModalOpen(false);
						}}
					>
						Annulla
					</Button>
				</Modal.Footer>
			</Modal>

			<ImportJobToolModal isModalOpen={isJobToolModalOpen} setIsModalOpen={setIsJobToolModalOpen} />

			<UploadChoiceModal
				activeKey={entity.entityType}
				show={!!selectedFileForUploadChoice && !uploadDataMutation.isError}
				mutation={uploadDataMutation}
				selectedFile={selectedFileForUploadChoice}
				setSelectedFile={setSelectedFileForUploadChoice}
			/>

			<RejectModal
				show={isTooBig}
				onHide={() => {
					setIsTooBig(false);
				}}
				message="Il file selezionato supera i 5 GB. Seleziona un file più piccolo."
			/>

			{errors.length > 0
				? errors.map((error) => (
						<RejectModal
							key={error.key}
							show={error.show}
							message={error.message || 'Si è verificato un errore durante il caricamento.'}
							onHide={error.onHide}
						/>
					))
				: null}

			{successes.length > 0
				? successes.map((success) => (
						<SuccessModal
							key={success.key}
							show={success.show}
							message={success.successMessage || 'Caricamento completato con successo.'}
							onHide={success.onHide}
						/>
					))
				: null}
		</>
	);
};

export default UploadEntitiesModal;
